// Step 6 — Payment. Mirrors wallet/AddFunds.tsx Stripe flow exactly:
// prepare-payment → loadStripe(publishable_key) → <Elements clientSecret> →
// <PaymentElement/> → confirmPayment({redirect:'if_required'}). Card data goes
// straight to Stripe — never to our backend.
//
// Payment is webhook-authoritative: a browser confirm alone does NOT mean paid.
// After confirm we POLL payment-status until `paid` before showing success.

import { useEffect, useRef, useState } from 'react'
import {
  loadStripe,
  type Stripe,
  type StripeElementsOptions,
} from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { CheckCircle2, ShieldCheck, Loader2, AlertCircle } from 'lucide-react'
import {
  fetchPaymentStatus,
  preparePayment,
  PublicApiError,
  type PreparePaymentResponse,
} from '../../api/company-registration-public'
import { formatCurrency } from '../../lib/currency'
import { useT } from '../../i18n'
import { START_COMPANY_STRINGS } from '../../i18n/start-company/form'

type Phase =
  | 'preparing'
  | 'ready'
  | 'confirming'
  | 'pending_confirmation'
  | 'success'
  | 'failed'

export function PaymentStep({ humanReference }: { humanReference: string }) {
  const t = useT(START_COMPANY_STRINGS)
  // Returned from a redirect payment method (3DS / bank redirect): Stripe
  // appended redirect_status — start straight in the webhook-authoritative
  // polling phase (lazy init; the effect below strips the URL params).
  const [phase, setPhase] = useState<Phase>(() => {
    const rs = new URLSearchParams(window.location.search).get('redirect_status')
    return rs === 'succeeded' || rs === 'processing' ? 'confirming' : 'preparing'
  })
  const [prep, setPrep] = useState<PreparePaymentResponse | null>(null)
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const startedRef = useRef(false)

  // Prepare the PaymentIntent once when the step mounts.
  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    // Returned from a redirect payment method (3DS / bank redirect): Stripe
    // appended redirect_status + the intent. Do NOT re-prepare (that would mint a
    // second intent) — the lazy phase init above already routed to the
    // webhook-authoritative poller; here just strip the client_secret + marker
    // out of the address bar / history.
    const params = new URLSearchParams(window.location.search)
    const redirectStatus = params.get('redirect_status')
    if (redirectStatus) {
      window.history.replaceState({}, '', window.location.pathname)
      if (redirectStatus === 'succeeded' || redirectStatus === 'processing') {
        return
      }
      // failed / requires_payment_method → let them retry via a fresh prepare.
    }
    let cancelled = false
    void (async () => {
      try {
        const data = await preparePayment()
        if (cancelled) return
        if (!data.publishable_key) throw new Error('Stripe is not configured')
        setPrep(data)
        setStripePromise(loadStripe(data.publishable_key))
        setPhase('ready')
      } catch (e) {
        if (cancelled) return
        // A charge already exists (payment succeeded / intent open) → the webhook
        // is authoritative; poll rather than showing an error or re-charging.
        if (e instanceof PublicApiError && e.code === 'already_paid') {
          setPhase('confirming')
          return
        }
        setError(e instanceof Error ? e.message : t.payment.prepareError)
        setPhase('failed')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [t.payment.prepareError])

  // Poll for webhook-authoritative payment once the browser confirm resolves.
  useEffect(() => {
    if (phase !== 'confirming') return
    let cancelled = false
    let attempts = 0
    let timer: number | undefined
    const tick = async () => {
      attempts += 1
      try {
        const s = await fetchPaymentStatus()
        if (cancelled) return
        if (s.paid) {
          setPhase('success')
          return
        }
        if (s.payment_status === 'failed') {
          setPhase('failed')
          setError(t.payment.failedBody)
          return
        }
      } catch {
        // transient — keep polling
      }
      if (attempts >= 40) {
        // ~2 min ceiling: the webhook is just slow (retry/backoff/test mode). Stop
        // spinning and reassure — the payment is recorded server-side and the
        // applicant has their reference + an email confirmation to follow.
        if (!cancelled) setPhase('pending_confirmation')
        return
      }
      if (!cancelled) timer = window.setTimeout(tick, 3000)
    }
    void tick()
    return () => {
      cancelled = true
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [phase, t.payment.failedBody])

  const elementsOptions: StripeElementsOptions | null = prep
    ? { clientSecret: prep.client_secret, appearance: { theme: 'stripe' } }
    : null

  if (phase === 'success') {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-6 text-center">
        <CheckCircle2
          className="mx-auto mb-3 text-emerald-600"
          size={40}
          aria-hidden="true"
        />
        <h2 className="text-lg font-semibold text-[var(--color-text)]">
          {t.payment.successTitle}
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-[var(--color-text-muted)]">
          {t.payment.successBody(humanReference)}
        </p>
        <div className="mt-4 inline-flex flex-col items-center rounded-md bg-[var(--color-surface)] px-4 py-2 ring-1 ring-emerald-200">
          <span className="text-xs text-[var(--color-text-muted)]">{t.payment.referenceLabel}</span>
          <span className="font-mono text-base font-semibold text-[var(--color-text)]">
            {humanReference}
          </span>
        </div>
      </div>
    )
  }

  if (phase === 'confirming') {
    return (
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-6 text-center">
        <Loader2
          className="mx-auto mb-3 animate-spin text-brand-600"
          size={36}
          aria-hidden="true"
        />
        <h2 className="text-base font-semibold text-[var(--color-text)]">
          {t.payment.confirming}
        </h2>
        <p className="mx-auto mt-2 max-w-sm text-sm text-[var(--color-text-muted)]">
          {t.payment.cardNote}
        </p>
      </div>
    )
  }

  if (phase === 'pending_confirmation') {
    return (
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-6 text-center">
        <ShieldCheck
          className="mx-auto mb-3 text-emerald-600"
          size={36}
          aria-hidden="true"
        />
        <h2 className="text-base font-semibold text-[var(--color-text)]">
          {t.payment.slowTitle}
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-[var(--color-text-muted)]">
          {t.payment.slowBody(humanReference)}
        </p>
        <div className="mt-4 inline-flex flex-col items-center rounded-md bg-[var(--color-surface-muted)] px-4 py-2 ring-1 ring-slate-200">
          <span className="text-xs text-[var(--color-text-muted)]">{t.payment.referenceLabel}</span>
          <span className="font-mono text-base font-semibold text-[var(--color-text)]">
            {humanReference}
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-[var(--color-text)]">{t.payment.heading}</h2>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">{t.payment.intro}</p>
      </div>

      {prep && (
        <div className="flex items-center justify-between rounded-md bg-[var(--color-surface-muted)] px-4 py-3 ring-1 ring-slate-200">
          <span className="text-sm font-medium text-[var(--color-text)]">
            {prep.package_label}
          </span>
          <span className="font-mono text-base font-semibold text-[var(--color-text)]">
            {formatCurrency(prep.amount_cents / 100, prep.currency)}
          </span>
        </div>
      )}

      <p className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
        <ShieldCheck size={13} className="text-emerald-600" aria-hidden="true" />
        {t.payment.cardNote}
      </p>

      {phase === 'failed' && (
        <div className="flex items-start gap-2 rounded-md border border-[var(--color-danger)] bg-[var(--color-surface-muted)] p-3 text-sm text-[var(--color-danger)]">
          <AlertCircle size={15} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
          <div>
            <p className="font-medium">{t.payment.failedTitle}</p>
            <p className="text-xs">{error ?? t.payment.failedBody}</p>
          </div>
        </div>
      )}

      {phase === 'preparing' && (
        <div className="flex items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-6 text-sm text-[var(--color-text-muted)]">
          <Loader2 className="animate-spin" size={16} aria-hidden="true" />
          {t.common.loading}
        </div>
      )}

      {phase === 'ready' && stripePromise && elementsOptions && (
        <Elements stripe={stripePromise} options={elementsOptions}>
          <CheckoutForm
            payLabel={t.payment.payButton}
            processingLabel={t.payment.processing}
            onConfirmed={() => setPhase('confirming')}
            onError={(msg) => {
              setError(msg)
              setPhase('failed')
            }}
          />
        </Elements>
      )}
    </div>
  )
}

function CheckoutForm({
  payLabel,
  processingLabel,
  onConfirmed,
  onError,
}: {
  payLabel: string
  processingLabel: string
  onConfirmed: () => void
  onError: (msg: string) => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)

  async function pay() {
    if (!stripe || !elements) return
    setSubmitting(true)
    // A redirect method (3DS/bank) returns here; the marker makes the wizard
    // resume on the payment step so the poller re-mounts (redirect:'if_required'
    // keeps card-only payments inline, no navigation).
    const returnUrl = `${window.location.origin}${window.location.pathname}?cr_confirm=1`
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    })
    if (error) {
      onError(error.message ?? 'Please try another card.')
      setSubmitting(false)
      return
    }
    // Card confirmed in-browser (or requires no extra action). The webhook is
    // authoritative, so hand off to the poller regardless of the interim status.
    if (
      paymentIntent &&
      (paymentIntent.status === 'succeeded' ||
        paymentIntent.status === 'processing')
    ) {
      onConfirmed()
    } else {
      // requires_action etc. handled by Stripe UI — re-enable so the user retries.
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-4">
      <PaymentElement />
      <button
        type="button"
        disabled={!stripe || submitting}
        onClick={pay}
        className="w-full rounded-md bg-[var(--color-action)] px-4 py-3 text-sm font-semibold text-[var(--color-action-foreground)] transition-colors hover:bg-[var(--color-action-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitting ? processingLabel : payLabel}
      </button>
    </div>
  )
}
